/**
 * Query Key Factory Pattern
 *
 * Centralized way to create consistent query keys across the application.
 * Each resource has its own key factory with hierarchical structure.
 */

export const queryKeys = {
  // Base key for all queries
  all: ["veraciti-vendor"] as const,

  // Auth
  auth: () => [...queryKeys.all, "auth"] as const,
  currentUser: () => [...queryKeys.auth(), "me"] as const,

  // Vendor
  vendor: () => [...queryKeys.all, "vendor"] as const,
  vendorStats: () => [...queryKeys.vendor(), "stats"] as const,
  vendorCompanies: (filters?: any) => [...queryKeys.vendor(), "companies", filters] as const,
  vendorUsers: (filters?: any) => [...queryKeys.vendor(), "users", filters] as const,

  // Companies
  companies: () => [...queryKeys.all, "companies"] as const,
  company: (id: number) => [...queryKeys.companies(), id] as const,
  companyList: (filters?: any) => [...queryKeys.companies(), "list", filters] as const,

  // Company Types
  companyTypes: () => [...queryKeys.all, "companyTypes"] as const,
  companyTypeList: (filters?: any) => [...queryKeys.companyTypes(), "list", filters] as const,

  // Points of Sale (POS)
  pos: () => [...queryKeys.all, "pos"] as const,
  posItem: (id: number) => [...queryKeys.pos(), id] as const,
  posList: (filters?: any) => [...queryKeys.pos(), "list", filters] as const,

  // Users
  users: () => [...queryKeys.all, "users"] as const,
  user: (id: number) => [...queryKeys.users(), id] as const,
  userList: (filters?: any) => [...queryKeys.users(), "list", filters] as const,

  // Roles
  roles: () => [...queryKeys.all, "roles"] as const,
  role: (id: number) => [...queryKeys.roles(), id] as const,
  roleList: (filters?: any) => [...queryKeys.roles(), "list", filters] as const,

  // Grants (Permissions)
  grants: () => [...queryKeys.all, "grants"] as const,
  grant: (id: number) => [...queryKeys.grants(), id] as const,
  grantList: (filters?: any) => [...queryKeys.grants(), "list", filters] as const,

  // Permissions
  permissions: () => [...queryKeys.all, "permissions"] as const,
  permissionList: (filters?: any) => [...queryKeys.permissions(), "list", filters] as const,

  // SEFAZ
  sefaz: () => [...queryKeys.all, "sefaz"] as const,
  sefazStatus: () => [...queryKeys.sefaz(), "status"] as const,

  // Plans
  plans: () => [...queryKeys.all, "plans"] as const,
  plan: (id: number) => [...queryKeys.plans(), id] as const,
  planList: (filters?: any) => [...queryKeys.plans(), "list", filters] as const,

  // Modules
  modules: () => [...queryKeys.all, "modules"] as const,
  moduleList: (filters?: any) => [...queryKeys.modules(), "list", filters] as const,

  // Certificates
  certificates: () => [...queryKeys.all, "certificates"] as const,
  certificate: (id: number) => [...queryKeys.certificates(), id] as const,
  certificateList: (filters?: any) => [...queryKeys.certificates(), "list", filters] as const,

  // Reports
  reports: () => [...queryKeys.all, "reports"] as const,
  reportList: (filters?: any) => [...queryKeys.reports(), "list", filters] as const,

  // Branding
  branding: () => [...queryKeys.all, "branding"] as const,
  brandingDetail: (tenantId: number) => [...queryKeys.branding(), tenantId] as const,

  // Billing
  billing: () => [...queryKeys.all, "billing"] as const,
  billingMetrics: (tenantId: number, filters?: any) => [...queryKeys.billing(), "metrics", tenantId, filters] as const,
  billingMetricsList: (filters?: any) => [...queryKeys.billing(), "metrics", "list", filters] as const,

  // Analytics
  analytics: () => [...queryKeys.all, "analytics"] as const,
  analyticsRevenueSummary: (params?: any) => [...queryKeys.analytics(), "revenue", "summary", params] as const,
  analyticsRevenueOverTime: (params?: any) => [...queryKeys.analytics(), "revenue", "over-time", params] as const,
  analyticsRevenueByCompany: (params?: any) => [...queryKeys.analytics(), "revenue", "by-company", params] as const,
  analyticsOperationsStatus: (params?: any) => [...queryKeys.analytics(), "operations", "status", params] as const,
  analyticsTopProducts: (params?: any) => [...queryKeys.analytics(), "products", "top", params] as const,

  // Software
  software: () => [...queryKeys.all, "software"] as const,
  softwareList: (filters?: any) => [...queryKeys.software(), "list", filters] as const,

  // Scopes
  scopes: () => [...queryKeys.all, "scopes"] as const,
  scopeList: (filters?: any) => [...queryKeys.scopes(), "list", filters] as const,

  // Pre-registrations
  preregistrations: () => [...queryKeys.all, "preregistrations"] as const,
  preregistration: (id: number) => [...queryKeys.preregistrations(), id] as const,
  preregistrationList: (filters?: any) => [...queryKeys.preregistrations(), "list", filters] as const,

  // External services
  external: () => [...queryKeys.all, "external"] as const,
  cep: (cep: string) => [...queryKeys.external(), "cep", cep] as const,
  cities: (stateCode: string) => [...queryKeys.external(), "cities", stateCode] as const,
} as const

// Type helper for query keys
export type QueryKeys = typeof queryKeys
